import React, {useCallback, useEffect, useLayoutEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useSearchParams} from 'react-router-dom';
import {DeviceType} from '../../incase-domain/common/DeviceType.enum';
import {ELEMENT} from '../../incase-domain/elements/base/ElementTypes';
import {IPresentation} from '../../incase-domain/presentation';
import {ISlide} from '../../incase-domain/slide';
import {fetchPresentationForSlidePreview} from '../../service/presentation.service';
import {
    updateFullPresentationAction,
    updatePresentationAction,
    updatePresentationHasSlidesAction,
    updatePresentationRunning,
} from '../../store/actions/dashboard.actions';
import {setDesktopViewSelected, setSelectedDeviceTypeAction} from '../../store/actions/layout.actions';
import {resetPresentationReducerAction, updateCurrentSlide} from '../../store/actions/presentation.actions';
import {updateSpinnerLoadingAction} from '../../store/actions/spinner.actions';
import {loadedPresentationSelector} from '../../store/selectors/presentation.selectors';
import PresentationView from '../../components/presentationView/PresentationView';
import {SlidePreviewCaptureProvider} from '../../context/SlidePreviewCaptureContext';

export const SLIDE_PREVIEW_APPLY_EVENT = 'slide-preview-apply';

const HTML_FRAME_MOUNT_TIMEOUT_MS = 1000;
const HTML_FRAME_SETTLE_MS = 500;

function countHtmlFrames(slide: ISlide): number {
    return (slide.elements || []).reduce((count, element) => {
        if (element.type === ELEMENT.HTML) {
            return element.text ? count + 1 : count;
        }
        if (element.type === ELEMENT.FLIP_CARD) {
            const sides = Object.values(element.flipCardElement || {});
            return count + sides.filter((side) => side?.type === ELEMENT.HTML && side.text).length;
        }
        return count;
    }, 0);
}

function SlidePreviewCapturePage(): JSX.Element {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const [ready, setReady] = useState(false);
    const loadedPresentation = useSelector(loadedPresentationSelector);

    const presentationId = searchParams.get('presentationId') || '';
    const slideId = searchParams.get('slideId') || '';
    const exp = searchParams.get('exp') || '';
    const sig = searchParams.get('sig') || '';
    const [activeSlideId, setActiveSlideId] = useState(slideId);

    const applyPresentation = useCallback(
        (presentation: IPresentation): void => {
            const nextSlideId = String(presentation.slides?.[0]?._id || '');
            setReady(false);
            setActiveSlideId(nextSlideId);
            dispatch(updatePresentationAction({...presentation, found: true}));
            dispatch(updateFullPresentationAction(presentation));
            dispatch(updatePresentationHasSlidesAction(true));
            dispatch(updateCurrentSlide(0));
            dispatch(updatePresentationRunning(true));
        },
        [dispatch]
    );

    useLayoutEffect(() => {
        setReady(false);
        setActiveSlideId(slideId);
    }, [presentationId, slideId, exp, sig]);

    useEffect(() => {
        dispatch(updateSpinnerLoadingAction(false));
        dispatch(setDesktopViewSelected(true));
        dispatch(setSelectedDeviceTypeAction(DeviceType.DESKTOP));

        const onApply = (event: Event): void => {
            const presentation = (event as CustomEvent<IPresentation>).detail;
            if (!presentation?.slides?.[0]) {
                return;
            }
            applyPresentation(presentation);
        };
        window.addEventListener(SLIDE_PREVIEW_APPLY_EVENT, onApply);

        return () => {
            window.removeEventListener(SLIDE_PREVIEW_APPLY_EVENT, onApply);
        };
    }, [applyPresentation, dispatch]);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            if (!presentationId || !slideId || !exp || !sig) {
                return;
            }
            try {
                const presentation: IPresentation = await fetchPresentationForSlidePreview(presentationId, slideId, exp, sig);
                if (cancelled) {
                    return;
                }
                applyPresentation(presentation);
            } catch (error) {
                // do nothing
            }
        })();

        return () => {
            cancelled = true;
            dispatch(resetPresentationReducerAction());
        };
    }, [applyPresentation, dispatch, presentationId, slideId, exp, sig]);

    useEffect(() => {
        const slide = loadedPresentation?.slides?.[0];
        if (!slide || String(slide._id) !== String(activeSlideId)) {
            return () => {
                // do nothing
            };
        }
        let cancelled = false;
        const markReady = (): void => {
            if (!cancelled) {
                setReady(true);
            }
        };

        const waitForCanvas = (): Promise<void> =>
            new Promise((resolve) => {
                const tick = (): void => {
                    if (cancelled) {
                        return;
                    }
                    const canvas = document.querySelector('#canvas-holder');
                    if (canvas && canvas.getBoundingClientRect().width > 0) {
                        resolve();
                        return;
                    }
                    window.requestAnimationFrame(tick);
                };
                tick();
            });

        const expectedHtmlFrames = countHtmlFrames(slide);

        const waitForHtmlFrames = (): Promise<void> =>
            new Promise((resolve) => {
                if (!expectedHtmlFrames) {
                    resolve();
                    return;
                }
                const startedAt = performance.now();
                const tick = (): void => {
                    if (cancelled) {
                        return;
                    }
                    const frames = Array.from(document.querySelectorAll<HTMLElement>('#canvas-holder [data-html-loaded]'));
                    const mounted = frames.length >= expectedHtmlFrames || performance.now() - startedAt > HTML_FRAME_MOUNT_TIMEOUT_MS;
                    if (mounted && frames.every((frame) => frame.dataset.htmlLoaded === 'true')) {
                        window.setTimeout(resolve, HTML_FRAME_SETTLE_MS);
                        return;
                    }
                    window.requestAnimationFrame(tick);
                };
                tick();
            });

        const waitForImages = (): Promise<void> => {
            const images = Array.from(document.querySelectorAll('#canvas-holder img')) as HTMLImageElement[];
            if (!images.length) {
                return Promise.resolve();
            }
            return Promise.all(
                images.map(
                    (image) =>
                        new Promise<void>((resolve) => {
                            if (image.complete) {
                                resolve();
                                return;
                            }
                            image.addEventListener('load', () => resolve(), {once: true});
                            image.addEventListener('error', () => resolve(), {once: true});
                        })
                )
            ).then(() => undefined);
        };

        waitForCanvas()
            .then(waitForHtmlFrames)
            .then(waitForImages)
            .then(() => {
                window.requestAnimationFrame(() => {
                    window.requestAnimationFrame(markReady);
                });
            })
            .catch(markReady);

        const timeout = window.setTimeout(markReady, 8000);

        return () => {
            cancelled = true;
            window.clearTimeout(timeout);
        };
    }, [activeSlideId, loadedPresentation]);

    return (
        <SlidePreviewCaptureProvider>
            <div
                className="p-player p-player--desktop p-player--preview"
                data-slide-preview-presentation-id={loadedPresentation?._id || presentationId}
                data-slide-preview-id={activeSlideId}
                data-slide-preview-ready={ready ? 'true' : 'false'}
            >
                {loadedPresentation?.slides?.[0] ? <PresentationView /> : null}
            </div>
        </SlidePreviewCaptureProvider>
    );
}

export default SlidePreviewCapturePage;
